import DOMPurify from 'dompurify'

/**
 * Sanitisation for the constrained rich text produced by `UIRichTextEditor`.
 *
 * The editor's schema is deliberately tiny — headings, paragraphs, bold, italic,
 * links and paragraph alignment — so the allow-list can be exhaustive rather
 * than "everything minus the scary bits". Copy is admin-authored, but "admin"
 * is not "trusted to inject script into every user's session", so this runs on
 * the way in (before save) *and* on the way out (before `v-html`).
 */

export const RICH_TEXT_ALLOWED_TAGS = [
  'p',
  'h3',
  'strong',
  'em',
  'a',
  'br',
] as const

const ALLOWED_TEXT_ALIGN = new Set(['left', 'center', 'right', 'justify'])

/**
 * Only `text-align` survives, and only with a known keyword — a raw `style`
 * pass-through would readmit `position: fixed` overlays and `url(...)` fetches.
 */
const stripDisallowedStyles = (root: Element) => {
  root.querySelectorAll('[style]').forEach((element) => {
    const align = (element as HTMLElement).style.textAlign
    element.removeAttribute('style')
    if (align && ALLOWED_TEXT_ALIGN.has(align)) {
      ;(element as HTMLElement).style.textAlign = align
    }
  })
}

/**
 * `target=_blank` without `rel` hands the opener a live `window.opener`
 * reference; force the safe pair onto every link rather than trusting the
 * editor to have set it.
 */
const hardenLinks = (root: Element) => {
  root.querySelectorAll('a').forEach((anchor) => {
    anchor.setAttribute('target', '_blank')
    anchor.setAttribute('rel', 'noopener noreferrer')
  })
}

/**
 * Sanitising is a full DOMPurify pass plus a DOM round trip, and the same body
 * string is asked for four times per keystroke — the editor's empty check, the
 * character counter, the publish blocker and the live preview all read the one
 * value. These are pure functions of a string, so caching the last few results
 * collapses that to a single pass without changing what any caller sees.
 *
 * Bounded so a long editing session cannot grow it without limit; a handful of
 * entries is enough to absorb the sibling reads of one keystroke.
 */
const CACHE_LIMIT = 8

const memoize = (compute: (_html: string) => string) => {
  const cache = new Map<string, string>()

  return (html: string): string => {
    const cached = cache.get(html)
    if (cached !== undefined) return cached

    const value = compute(html)
    // `Map` iterates in insertion order, so the first key is the oldest.
    if (cache.size >= CACHE_LIMIT) {
      const oldest = cache.keys().next().value
      if (oldest !== undefined) cache.delete(oldest)
    }
    cache.set(html, value)
    return value
  }
}

export const sanitizeRichText = memoize((html: string): string => {
  if (!html) return ''

  // `RETURN_DOM_FRAGMENT` lets the post-passes run on parsed nodes; re-reading
  // the string with a regex would be the classic sanitiser-bypass shape.
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...RICH_TEXT_ALLOWED_TAGS],
    ALLOWED_ATTR: ['href', 'style'],
    // Blocks `javascript:` / `data:` hrefs while leaving http(s) and mailto.
    ALLOWED_URI_REGEXP:
      /^(?:https?:|mailto:|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    RETURN_DOM_FRAGMENT: true,
  }) as unknown as DocumentFragment

  const host = document.createElement('div')
  host.appendChild(fragment)

  stripDisallowedStyles(host)
  hardenLinks(host)

  return host.innerHTML
})

/** Text-only projection — used for counters, summaries, and aria labels. */
export const richTextToPlainText = memoize((html: string): string => {
  if (!html) return ''
  const host = document.createElement('div')
  host.innerHTML = sanitizeRichText(html)
  return (host.textContent ?? '').replace(/\s+/g, ' ').trim()
})

/**
 * An editor left untouched still serialises to `<p><br></p>`, so "did the admin
 * write anything" has to be asked of the text, not the markup.
 */
export const isRichTextEmpty = (html: string): boolean =>
  richTextToPlainText(html).length === 0
